import Breadcrumbs from "@/components/Breadcrumbs";
import { assetUrl } from '@/utils/asset-url';
import Button from "@/components/Button";
import Portal from "@/components/Portal";
import { EnrolmentTypes } from "@/constants/use-case/enrolment";
import usePage from "@/hooks/use-page/index";
import { useLocation, useNavigate, useParams } from "@/lib/router-dom/index";
import PropTypes from "prop-types";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";

const SuccessMessage = ({ editType, isEnrolment, pitstopName, isAgentPrincipalPhase2Enabled, enrolmentType }) => {
	const getEnrolmentMessage = (editType, pitstopName) => {
		switch (editType) {
			case "Add":
				return (
					<>
						<div>New Principal(s) has been successfully added</div>
						<div>into {pitstopName || "Data Element list"}</div>
					</>
				);
			case "Remove":
				return (
					<>
						<div>Principal(s) has been successfully removed</div>
						<div>from {pitstopName || "Data Element list"}</div>
					</>
				);
			case "Add&Remove":
				return (
					<>
						<div>Your request to edit principal(s) for</div>
						<div>{pitstopName || "Data Element"} have been saved successfully</div>
					</>
				);
			default:
				return null;
		}
	};

	const getNonEnrolmentMessage = (editType, isAgentPrincipalPhase2Enabled, enrolmentType) => {
		if (editType === "Remove") {
			return <div>Principal(s) have been successfully removed</div>;
		} else {
			const provideOnBehalfOfPrincipal = EnrolmentTypes.provideOnBehalfOfPrincipal.toLowerCase();
			const consumeOnBehalfOfPrincipal = EnrolmentTypes.consumeOnBehalfOfPrincipal.toLowerCase();

			const agentEditSuccessMessage = `Your request to ${enrolmentType === provideOnBehalfOfPrincipal ? 'provide' : 'consume'} on behalf of the selected principal(s) for the data element has been submitted`;
			const consumerProviderEditSuccessMessage = `Your request for the Participant (Agent) to ${enrolmentType === EnrolmentTypes.consumer.toLowerCase() ? 'provide' : 'consume'} data on behalf of the selected Principal(s) for the data element has been submitted`;

			const agentPrincipalCopyright = (enrolmentType === provideOnBehalfOfPrincipal || enrolmentType === consumeOnBehalfOfPrincipal) ? agentEditSuccessMessage : consumerProviderEditSuccessMessage;
			const defaultCopyright = `Your request to consume on behalf of the selected principal(s) for the data element has been submitted`;

			return (
				<div>
					{isAgentPrincipalPhase2Enabled ? agentPrincipalCopyright : defaultCopyright}
				</div>
			);
		}
	};

	return (
		<>
			{isEnrolment
				? getEnrolmentMessage(editType, pitstopName)
				: getNonEnrolmentMessage(editType, isAgentPrincipalPhase2Enabled, enrolmentType)}
		</>
	);
};

SuccessMessage.propTypes = {
	editType: PropTypes.string,
	isEnrolment: PropTypes.bool,
	pitstopName: PropTypes.string,
	isAgentPrincipalPhase2Enabled: PropTypes.bool,
	enrolmentType: PropTypes.string,
};

const SubSuccessMessage = ({ isEnrolment }) => {
	if (!isEnrolment) {
		return <div>You will be notified of any updates by your registered email address.</div>
	}

	return <></>
}

SubSuccessMessage.propTypes = {
	isEnrolment: PropTypes.bool,
};

const Page = () => {
	/*=================================================
		General
	/================================================*/
	const { goTo } = useNavigate();
	const { currentState } = useLocation();

	const { useCaseName, dataElementName, pitstopName, enrolmentType, dataElementId, editType, isEnrolment = true } = currentState;
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	const { breadcrumbsItems } = usePage({
		title: "Edit Principal - Success",
		breadcrumbs: [
			"Dashboard",
			useCaseName,
			dataElementName,
			"Edit Principal",
		],
	})

	const { useCaseId } = useParams();

	/*=================================================
		Navigate
	/================================================*/

	function backToDashboard() {
		goTo(`/use-cases/${useCaseId}/dashboard/`)
	}

	function backToDataElementDetails() {
		if (enrolmentType && dataElementId) {
			goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/${enrolmentType}`)
		} else {
			backToDashboard();
		}
	}

	return (
		<>
			<div className="layout-feedback">
				<div className="layout-feedback__header principal__feedback__header">
					<SuccessMessage editType={editType} isEnrolment={isEnrolment} pitstopName={pitstopName} isAgentPrincipalPhase2Enabled={isAgentPrincipalPhase2Enabled} enrolmentType={enrolmentType} />
				</div>
				<div className="layout-feedback__subheader layout-feedback__subheader--center">
					<SubSuccessMessage isEnrolment={isEnrolment} />
				</div>
				<div className="layout-feedback__content">
					<img src={assetUrl("/img/success.png")} />
				</div>
				<div className="layout-feedback__footer">
					<Button
						sizing='Medium'
						text="Back to Use Case Dashboard"
						round
						ghost
						submit={() => backToDashboard()}
					/>
					<Button
						sizing="Medium"
						text="Back to Data Element Details"
						round
						submit={() => backToDataElementDetails()}
					/>
				</div>
			</div>
		</>
	);
};

export default Page;
